import { Fragment, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Network, Truck, BarChart3, Calendar, FileText, Database, ScanSearch, Tv } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSimulation } from '@/hooks/useSimulation';
import { t } from '@/i18n';
import { OmyaLogo } from '@/components/icons/OmyaLogo';
import { actionCount, currentView, isInSection, subViews } from './subViews';

// Current focus first; everything else is a concept (ideas and options, no real data yet).
const navigation = [
  {
    section: t('nav.focus'),
    items: [
      { name: t('nav.trucks'), href: '/kamiony', icon: Truck, countKey: 'trucks' },
      { name: t('nav.stats'), href: '/statistiky', icon: BarChart3 },
    ],
  },
  {
    section: t('nav.concepts'),
    concept: true,
    items: [
      { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
      { name: t('nav.supplyChain'), href: '/supply-chain', icon: Network },
      { name: t('nav.planning'), href: '/planovani', icon: Calendar },
      { name: t('nav.orders'), href: '/objednavky', icon: FileText, countKey: 'orders' },
      { name: t('nav.sapIntegration'), href: '/sap', icon: Database },
      { name: t('nav.branchDetection'), href: '/detekce-vetvi', icon: ScanSearch },
      { name: t('nav.visualFactory'), href: '/visual-factory', icon: Tv, external: true },
    ],
  },
];

export const Sidebar = () => {
  const location = useLocation();
  const { orders } = useSimulation();

  const counts = useMemo(() => ({
    // One number: the work queue. Shown on Akce when Arrivals is open, on Arrivals otherwise.
    trucks: actionCount,
    actions: actionCount,
    orders: orders.filter(o => o.status === 'pending').length,
  }), [orders]);

  return (
    <div className="hidden lg:flex fixed left-0 top-0 h-screen w-60 bg-primary text-white flex-col z-20">
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <OmyaLogo className="h-6" color="white" />
            <span className="text-[10px] text-white/70 tracking-wider">{t('layout.digitalPlant')}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-3">
          {navigation.map((section) => (
            <div key={section.section} className={cn('mb-6', section.concept && 'pt-4 border-t border-white/10')}>
              <div className="text-[11px] uppercase tracking-widest font-semibold text-white/70 px-3 mb-2">
                {section.section}
              </div>
              {section.concept && (
                <div className="text-[11px] text-white/60 px-3 -mt-1 mb-2">{t('nav.conceptsHint')}</div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const isActive = isInSection(location.pathname, item.href);
                  const views = isActive ? subViews[item.href] : null;
                  const count = item.countKey && !views ? counts[item.countKey] : 0;
                  if (item.external) {
                    return (
                      <a
                        key={item.name}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ease-out',
                          'text-white/75 hover:text-white hover:bg-white/10',
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.name}</span>
                      </a>
                    );
                  }
                  return (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-300 ease-out',
                          // With sub-items only the current sub-item is highlighted; the parent is just bold.
                          isActive && views ? 'text-white font-semibold'
                            : isActive ? 'bg-white/15 text-white'
                            : 'text-white/75 hover:text-white hover:bg-white/10'
                        )}
                      >
                        <item.icon className="h-4 w-4" />
                        <span>{item.name}</span>
                        {count > 0 && (
                          <span className="ml-auto text-[10px] font-bold bg-white/15 px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                            {count}
                          </span>
                        )}
                      </Link>
                      {views && (
                        <div className="mt-1 mb-2 ml-[22px] border-l border-white/15 space-y-0.5">
                          {views.map((v, i) => {
                            const current = currentView(location.pathname, item.href).key === v.key;
                            const startsSetup = v.group === 'setup' && views[i - 1]?.group !== 'setup';
                            return (
                              <Fragment key={v.key}>
                              {startsSetup && (
                                <div className="pl-4 pt-3 pb-1 text-[10px] uppercase tracking-widest text-white/60 border-t border-white/10 mt-2">
                                  {t('nav.planSetup')}
                                </div>
                              )}
                              <Link
                                to={v.path}
                                aria-current={current ? 'page' : undefined}
                                className={cn(
                                  'block pl-4 pr-3 py-1.5 text-[13px] rounded-r-lg -ml-px border-l-2',
                                  current
                                    ? 'text-white font-medium border-l-accent bg-white/10'
                                    : 'text-white/70 border-l-transparent hover:text-white',
                                )}
                              >
                                <span className="flex items-center">
                                  {t(v.label)}
                                  {v.count && counts[v.count] > 0 && (
                                    <span className="ml-auto text-[10px] font-bold bg-white/15 px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                                      {counts[v.count]}
                                    </span>
                                  )}
                                </span>
                              </Link>
                              </Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/10">
          <div className="text-[10px] text-white/60 text-center">
            v3.0.0 DEMO
          </div>
        </div>
    </div>
  );
};
